import 'twin.macro'
import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CgInfo } from 'react-icons/cg'

import {
  useListenStripeProductResponseQuery,
  useSetProductCurrencyMutation,
  useSetStripeRequestMutation,
} from 'modules/ProductInfo/productInfoApi'
import { useSetCoachOrgSlugMutation } from 'modules/PageBuilder/pageBuilderApi'

import { Input } from 'common/components/Input/Input'
import { Button } from 'common/components/Button/Button'
import { useFormRefsControl } from 'common/components/RefsControl/FormRefsControl/useFormRefsControl'
import { useEventListener } from 'common/hooks/useEventListener'

import { reservedRoutes } from 'modules/ProductInfo/constants/slugConstants'
import { createUID } from 'common/utils/createUID'
import { useAlert } from 'common/components/Alert/hooks/useAlert'
import { Tooltip } from 'common/components/Tooltip/Tooltip'
import { CURRENCY_CODE } from 'common/constants/currencyConstants'
import { getStandardizedName } from 'common/utils/stringUtils'
import { SELL } from 'modules/App/components/Header'
import CurrencyInput from '../CurrencyInput/CurrencyInput'

const nonAlphaNumericalRegex = /[\W_]+/g

export function GenerateStripeProduct({
  coachOrgId,
  allOrgSlugs,
  coachOrgName = '',
  isGeneratingProduct,
  setIsGeneratingProduct,
}) {
  const navigate = useNavigate()
  const { createAlert } = useAlert()

  const [productName, setProductName] = useState(coachOrgName)
  const [requestId, setRequestId] = useState(null)
  const [selectedCurrency, setSelectedCurrency] = useState(CURRENCY_CODE.USD)
  const [nameError, setNameError] = useState(null)
  const [isSlugUpdating, setIsSlugUpdating] = useState(false)
  const [slugError, setSlugError] = useState(null)
  const slugsTaken = Object.values(allOrgSlugs)
  const currOrgSlug = allOrgSlugs?.[coachOrgId] || coachOrgName.replace(nonAlphaNumericalRegex, '-').toLowerCase()
  const [coachSlug, setCoachSlug] = useState(currOrgSlug)

  const [setStripeRequest] = useSetStripeRequestMutation()
  const [setCurrency] = useSetProductCurrencyMutation()
  const [setCoachOrgSlug] = useSetCoachOrgSlugMutation()

  const { data: stripeResponse } = useListenStripeProductResponseQuery({ coachOrgId })
  const stripeResponsePending = stripeResponse?.requestKey === requestId && stripeResponse?.status === 'pending'
  const stripeResponseSuccess = stripeResponse?.requestKey === requestId && stripeResponse?.status === 'success'
  const stripeResponseError = stripeResponse?.requestKey === requestId && stripeResponse?.status === 'error'

  const isUpdating = requestId || stripeResponsePending || isSlugUpdating || isGeneratingProduct

  const updateSlug = async () => {
    const stdCoachSlug = getStandardizedName(coachSlug)
    const isSlugTaken = slugsTaken.find((slug) => getStandardizedName(slug) === getStandardizedName(stdCoachSlug))
    const isOwnSlug = getStandardizedName(allOrgSlugs?.[coachOrgId]) === stdCoachSlug
    if (reservedRoutes[stdCoachSlug] || (isSlugTaken && !isOwnSlug)) {
      setSlugError('This handle is taken, please choose a different handle.')
      setIsGeneratingProduct(false)
      return { success: false }
    } else {
      setIsSlugUpdating(true)
      await setCoachOrgSlug({ coachOrgId, slug: stdCoachSlug })
      setIsSlugUpdating(false)
      return { success: true }
    }
  }

  const handleCreate = async () => {
    setIsGeneratingProduct(true)
    const { success } = await updateSlug()
    if (success) {
      const uid = createUID()
      setRequestId(uid)
      await setStripeRequest({
        requestId: uid,
        coachOrgId,
        type: 'CREATE_PRODUCT',
        payload: {
          name: productName,
        },
      })
      await setCurrency({ coachOrgId, currency: selectedCurrency })
    }
  }

  useEffect(() => {
    if (stripeResponseError) {
      setIsGeneratingProduct(false)
      setRequestId(null)
      const errorMsg = stripeResponse?.errorMsg || 'Product creation failed!'
      createAlert({ text: errorMsg, type: 'danger' })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripeResponseError])

  useEffect(() => {
    if (stripeResponseSuccess) {
      setIsGeneratingProduct(false)
      setRequestId(null)
      createAlert({ text: 'Product created!', type: 'success' })
      navigate(SELL)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripeResponseSuccess])

  const { addInputRef, moveFocusOnKeyPress } = useFormRefsControl()
  const productNameRef = useRef()
  const coachSlugRef = useRef()
  const currencyInputRef = useRef()
  const createBtnRef = useRef()
  const inputRefsSortMethod = ['productName', 'coachSlug', 'currency', 'createBtn']
  useEffect(() => {
    addInputRef({ ref: productNameRef, name: 'productName', sortMethod: inputRefsSortMethod })
    addInputRef({ ref: coachSlugRef, name: 'coachSlug', sortMethod: inputRefsSortMethod })
    addInputRef({ ref: currencyInputRef, name: 'currency', sortMethod: inputRefsSortMethod })
    addInputRef({
      ref: createBtnRef,
      name: 'createBtn',
      sortMethod: inputRefsSortMethod,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEventListener('keydown', moveFocusOnKeyPress)

  return (
    <div>
      <h2 className='font-bold text-xl mb-2'>Create New Product</h2>
      <p className='text-gray-500 mb-4'>
        Before you begin creating pricing plans, we want you to add your product name, link and select currency.
        Selected name will be visible on your Stripe checkout page. You will be able to edit name and link afterwards.
        To edit currency you will have to contact our support team.
      </p>
      <div className='mb-4'>
        <div className='flex items-center mb-1'>
          <label htmlFor='productName' className='font-semibold text-tBlack cursor-pointer mr-1'>
            Product name
          </label>
          <Tooltip
            content='The product name will be shown to users when checking out through Stripe'
            triggerClasses='cursor-pointer text-gray-500 hover:text-tGreen'
          >
            <CgInfo className='w-4 h-4' />
          </Tooltip>
        </div>
        <Input
          inputRef={productNameRef}
          name='productName'
          placeholder='Enter your product name'
          value={productName}
          error={Boolean(nameError)}
          onChange={(e) => {
            if (nameError) {
              setNameError(null)
            }

            if (!e?.target?.value?.length) {
              setNameError('Product name is required')
            }

            setProductName(e.target.value)
          }}
          autoFocus={true}
        />
        {Boolean(nameError) && <div className='text-xs mt-1 text-tRed'>{nameError}</div>}
      </div>
      <div className='mb-4'>
        <div className='flex items-center mb-1'>
          <label htmlFor='productName' className='font-semibold text-tBlack cursor-pointer mr-1'>
            Your link
          </label>
          <Tooltip
            content='This will set your page links. You can edit this later, but avoid changing too often as it can negatively affect your page SEO.'
            triggerClasses='cursor-pointer text-gray-500 hover:text-tGreen'
          >
            <CgInfo className='w-4 h-4' />
          </Tooltip>
        </div>
        <Input
          inputRef={coachSlugRef}
          name='coachSlug'
          placeholder='Enter your link handle'
          value={coachSlug}
          onChange={(e) => {
            if (slugError) {
              setSlugError(null)
            }
            const value = e.target.value.replace(nonAlphaNumericalRegex, '-').toLowerCase()
            setCoachSlug(value)
            if (!value?.length) {
              setSlugError('Link handle is required')
            }
          }}
          disabled={isUpdating}
          error={Boolean(slugError)}
          maxLength={40}
        />
        {Boolean(slugError) && <div className='text-xs mt-1 text-tRed'>{slugError}</div>}
      </div>
      <div className='mb-6'>
        <CurrencyInput
          inputRef={currencyInputRef}
          selectedCurrency={selectedCurrency}
          setSelectedCurrency={setSelectedCurrency}
        />
      </div>
      <div className='flex items-center justify-end'>
        {isUpdating && <p className='mr-2 text-xs text-gray-500'>This may take a few seconds...</p>}
        <Button
          ref={createBtnRef}
          onClick={handleCreate}
          disabled={Boolean(slugError) || Boolean(nameError)}
          loading={isUpdating}
        >
          Create
        </Button>
      </div>
    </div>
  )
}
